//@ts-strict-ignore
/**
 * Check if this Sales Order Line can be archived.
 *  I.e. Must be capable of changing the Product (Not assigned, allocated, etc) & can't already be archived for obvious reasons.
 * @param salesOrderLine  Sales Order Line to check
 * @returns {boolean|*}   True if the line can be archived.
 */
import {
  ISalesOrderLine,
  ProductClass,
  SalesOrderLineStatus,
  PriceOrigin,
  IPurchaseOrderLine,
  PurchaseOrderLineStatus,
} from '../graphql/schema/p4types';

//Any status that could be considered 'processed' and hence should block editing of various elements (I.e. quantity, components, line-deletion etc).
export const PROCESSED_SALES_ORDER_LINE_STATUSES = [
  SalesOrderLineStatus.Reserved,
  SalesOrderLineStatus.Dispatched,
  SalesOrderLineStatus.AssignedAssembly,
  SalesOrderLineStatus.AssignedShortage,
  SalesOrderLineStatus.Assigned,
  SalesOrderLineStatus.Invoiced,
  SalesOrderLineStatus.InProduction,
];

export const checkLineCanArchive = (salesOrderLine?: ISalesOrderLine) => {
  return !salesOrderLine?.archived && !checkLineIsProcessed(salesOrderLine?.salesOrderLineStatus);
};

export const checkLinePriceApproved = (salesOrderLine?: ISalesOrderLine) => {
  return salesOrderLine?.priceApproved;
};

export const checkLineManualPrice = (salesOrderLine?: ISalesOrderLine) => {
  return salesOrderLine?.priceOrigin == PriceOrigin.Manual || salesOrderLine?.priceOrigin == PriceOrigin.ManualDiscount;
};

export const checkLinePriceApprovalButton = (salesOrderLine?: ISalesOrderLine) => {
  return checkLineManualPrice(salesOrderLine) && !salesOrderLine?.priceApproved;
};

export const checkLineIsProcessed = (
  salesOrderLineStatuses,
  allowedStatuses: Array<SalesOrderLineStatus> = PROCESSED_SALES_ORDER_LINE_STATUSES
) => {
  return salesOrderLineStatuses?.some((value) => allowedStatuses.includes(value)) ?? false;
};

/**
 * Check if the Product can still be changed on this Sales Order Line.
 *  I.e. If there are no statuses yet, or the status is at the Quoted, Enquiring, Awaiting Allocation stages, return true.
 * @param salesOrderLine  Sales Order Line to check
 * @returns {boolean|*}   True if the Product can still be changed.
 */
export const checkLineCanProductChange = (salesOrderLine?: ISalesOrderLine) => {
  //P4-3954 - Prevent changing of product on persisted lines if the Product is a kit.
  //        - (User should delete the line & add a new one instead).
  const isNotKitOrIsNew = ProductClass.Kit !== salesOrderLine?.product?.productClass || !salesOrderLine?.id;
  //Additional argument provided to 'checkLineIsProcessed' because the system should automatically de-allocate during pre-save if required - So 'allocated'/'in-production' are permitted.
  const isNotProcessed =
    !salesOrderLine?.salesOrderLineStatus ||
    salesOrderLine.salesOrderLineStatus.length === 0 ||
    !checkLineIsProcessed(salesOrderLine.salesOrderLineStatus, [
      SalesOrderLineStatus.Reserved,
      SalesOrderLineStatus.Allocated,
      SalesOrderLineStatus.InProduction,
      SalesOrderLineStatus.Dispatched,
      SalesOrderLineStatus.AssignedAssembly,
      SalesOrderLineStatus.AssignedShortage,
      SalesOrderLineStatus.Assigned,
      SalesOrderLineStatus.Invoiced,
    ]);

  const isHasEverBeenAssigned = !!salesOrderLine?.assignments?.length;

  return isNotKitOrIsNew && isNotProcessed && !isHasEverBeenAssigned;
};

/**
 * Determines if a sales order line is fully invoiced.
 * A sales order line is considered fully invoiced if all statuses in the `salesOrderLineStatus` array
 * are equal to `SalesOrderLineStatus.Invoiced`.
 *
 * @param salesOrderLine  - Sales Order Line to check
 * @returns - True if line fully invoiced.
 */
export const isFullyInvoiced = (salesOrderLine?: ISalesOrderLine): boolean => {
  return !!(
    salesOrderLine?.salesOrderLineStatus &&
    salesOrderLine.salesOrderLineStatus.length != 0 &&
    salesOrderLine.salesOrderLineStatus.every((status) => SalesOrderLineStatus.Invoiced === status)
  );
};

/**
 * Determines if a sales order line has any assignments.
 *
 * @param salesOrderLine - Sales Order Line to check
 * @returns - True if the line has at least one assignment.
 */
export const hasAssignments = (salesOrderLine?: ISalesOrderLine): boolean => {
  return !!salesOrderLine?.assignments?.some((assignment) => !assignment?.archived);
};

export const isFullyReceived = (purchaseOrderLine: IPurchaseOrderLine): boolean => {
  return !!(
    purchaseOrderLine.purchaseOrderLineStatus &&
    purchaseOrderLine.purchaseOrderLineStatus.length !== 0 &&
    purchaseOrderLine.purchaseOrderLineStatus.every(
      (status) => PurchaseOrderLineStatus.Received === status || PurchaseOrderLineStatus.ConsideredComplete === status
    )
  );
};
